import type { FC } from 'react'
import { Fragment } from 'react'

import { AuthClient } from '@skytech/auth'
import { useQuery } from '@tanstack/react-query'
import AccountCircleIcon from 'mdi-react/AccountCircleIcon'
import BellIcon from 'mdi-react/BellIcon'
import LogoutIcon from 'mdi-react/LogoutIcon'
import { Link } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useProfile } from '@/hooks/user'
import { commonQueries } from '@/resources/queries'
import { commonSystemMessagesResources } from '@/resources/system-messages'
import { APP_ROUTES } from '@/util/app-routes'

import { useNavBarForCustomer } from './useNavBarForCustomer'

import './mobile-side-bar.scss'

const MobileSideBar: FC<{ onBarClose: () => void }> = ({ onBarClose }) => {
  const profile = useProfile()
  const { locale, whiteLabel } = useAppContext()

  const routes = useNavBarForCustomer()

  if (!routes) throw new Error('No routes found for NavBar!')

  const readSystemMessages = localStorage.getItem('read-system-messages')
  const seenMessages = readSystemMessages ? JSON.parse(readSystemMessages) : undefined

  const { data: systemMessages } = useQuery(
    commonQueries.systemMessages(),
    commonSystemMessagesResources.getList
  )

  const localeBasedMessages = systemMessages?.filter(message => message.locale === locale)
  const displayedMessage = localeBasedMessages?.[0]
  const isNotificationRead = Boolean(
    displayedMessage && seenMessages?.includes(displayedMessage?.id)
  )

  const handleLogOut = () => {
    AuthClient.logOut()
    onBarClose()
  }

  return (
    <nav className={`mobile-side-bar ${whiteLabel}`}>
      <div className="primary">
        <ul>
          {routes.map(nav => {
            const subRoutes = nav.subRoutes
            // Mobile view doesn't support submenu items, leads to a page with links to these instead
            const path = subRoutes ? `menu${nav.route}` : nav.route

            return (
              <Fragment key={nav.route}>
                <li key={nav.route}>
                  <Link to={path} onClick={onBarClose} onKeyDown={onBarClose}>
                    {nav.Icon && nav.Icon}
                    {nav.label}
                  </Link>
                </li>
              </Fragment>
            )
          })}

          <li className="log-out-info">
            <button onClick={handleLogOut}>
              <LogoutIcon />
              <Translation id="Log out" />
            </button>
          </li>

          <li>
            <Link to={APP_ROUTES.SYSTEM_MESSAGES} onClick={onBarClose} onKeyDown={onBarClose}>
              <div
                className={`system-message-icon ${
                  displayedMessage && !isNotificationRead ? 'is-ringing' : ''
                }`}
              >
                <BellIcon />
              </div>

              <Translation id="Notifications" />
            </Link>
          </li>

          <li className="profile-info-tile">
            <div className="profile-info">
              <div>
                <AccountCircleIcon />
              </div>

              <div>
                <span className="name">{profile?.name}</span>
                <div className="email">{profile?.email}</div>
                <span className="phone-no">{profile?.phoneNumber}</span>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </nav>
  )
}

export default MobileSideBar
